<template>
  <div :data-id="panelId" class="flex flex-col pb-16">
    <div
      :data-id="panelId"
      class="sticky top-0 z-10 flex flex-col bg-pure-white"
    >
      <div :data-id="panelId" class="flex flex-col gap-2 px-6 py-6">
        <div :data-id="panelId" class="flex items-center justify-between gap-4">
          <BlTextTitle4 :data-id="panelId" text="Available credits" />
          <BlIcon icon="close" :clickable="true" />
        </div>
      </div>

      <div :data-id="panelId" class="flex flex-wrap gap-2 px-6">
        <div
          :data-id="panelId"
          v-for="item in itemOptions"
          :key="item.value"
          class="px-2 py-1 rounded-full cursor-pointer"
          :class="[
            selectedId === item.value
              ? 'bg-light-purple-400'
              : 'bg-slate-200 hover:bg-slate-400'
          ]"
          @click="selectedId = item.value"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="item.label"
            :class="{ 'text-pure-white': selectedId === item.value }"
          />
        </div>
      </div>
    </div>

    <div
      :data-id="panelId"
      v-if="!availableItems.length"
      class="grid px-16 h-128 place-items-center"
    >
      <BlTextBody1
        :data-id="panelId"
        text="All active credits will appear here once you add credits to the client."
        class="text-center"
      />
    </div>

    <div :data-id="panelId" v-if="selected" class="flex flex-col px-6">
      <!-- Current scheduled sessions -->
      <div
        :data-id="panelId"
        class="flex items-center justify-between gap-2 py-3 border-b border-slate-400"
      >
        <BlTextBody2
          :data-id="panelId"
          text="Current scheduled sessions"
          class="text-slate-700"
        />
        <BlTextBody2
          :data-id="panelId"
          :text="`${selected.scheduled_sessions} session(s)`"
          class="text-slate-1000"
        />
      </div>
      <!-- Total available credits -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex items-center justify-between gap-2">
          <BlTextBody2
            :data-id="panelId"
            text="Total available credits"
            class="text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${selected.total_available_credits} credit(s)`"
            class="text-slate-1000"
          />
        </div>
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${transaction.total_available_credits} credit(s)`"
            class="flex-shrink-0 text-slate-700"
          />
        </div>
      </div>
      <!-- Total reserved credits -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex items-center justify-between gap-2">
          <BlTextBody2
            :data-id="panelId"
            text="Total reserved credits"
            class="text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${selected.total_reserved_credits} credit(s)`"
            class="text-slate-1000"
          />
        </div>
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${transaction.total_reserved_credits} credit(s)`"
            class="flex-shrink-0 text-slate-700"
          />
        </div>
      </div>
      <!-- Total unused credits -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex items-center justify-between gap-2">
          <BlTextBody2
            :data-id="panelId"
            text="Total unused credits"
            class="text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${selected.total_unused_credits} credit(s)`"
            class="text-slate-1000"
          />
        </div>
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="`${transaction.total_unused_credits} credit(s)`"
            class="flex-shrink-0 text-slate-700"
          />
        </div>
      </div>
      <!-- Current balance -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex items-center justify-between gap-2">
          <BlTextBody2
            :data-id="panelId"
            text="Current balance"
            class="text-slate-700"
          />
          <div :data-id="panelId">
            <BlTextBody2
              :data-id="panelId"
              :text="currentCenter.currency_sym"
              class="text-slate-1000"
            />
            <BlTextBody2
              :data-id="panelId"
              :text="priceFormats(selected.current_balance)"
              class="text-slate-1000"
            />
          </div>
        </div>
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <div :data-id="panelId">
            <BlTextBody2
              :data-id="panelId"
              :text="currentCenter.currency_sym"
              class="text-slate-700"
            />
            <BlTextBody2
              :data-id="panelId"
              :text="priceFormats(transaction.current_balance)"
              class="flex-shrink-0 text-slate-700"
            />
          </div>
        </div>
      </div>
      <!-- Expiration date -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <div :data-id="panelId" class="flex items-center gap-2">
          <BlTextBody2
            :data-id="panelId"
            text="Expiration date"
            class="text-slate-700"
          />
          <BlTooltip>
            <template v-slot:tooltip>
              <div class="flex flex-col w-40 gap-1 text-center">
                The expiration date is the deadline to use credits for booking;
                once booked, they stay valid even if the appointment occurs
                later.
              </div>
            </template>
            <BlIcon icon="info" class="w-4 h-4" />
          </BlTooltip>
        </div>
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            v-if="transaction.expiration_date === null"
            :text="`No expiration date`"
            class="text-slate-1000"
          />
          <BlTextBody2
            :data-id="panelId"
            v-else
            :text="
              displayDate({
                date: transaction.expiration_date,
                format: 'DD MMMM YYYY'
              })
            "
            class="text-slate-1000"
          />
        </div>
      </div>
      <!-- Date created -->
      <div
        :data-id="panelId"
        class="flex flex-col gap-1 py-3 border-b border-slate-400"
      >
        <BlTextBody2
          :data-id="panelId"
          text="Date created"
          class="text-slate-700"
        />
        <div
          :data-id="panelId"
          v-for="transaction in selected.credit_transactions"
          :key="transaction.id"
          class="flex items-center justify-between gap-2 truncate"
        >
          <BlTextBody2
            :data-id="panelId"
            :text="`ID ${transaction.credit_transaction_id}`"
            class="truncate text-slate-700"
          />
          <BlTextBody2
            :data-id="panelId"
            :text="
              displayDate({
                date: transaction.created_at,
                format: 'DD MMMM YYYY'
              })
            "
            class="text-slate-1000"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlTooltip from "../../BlTooltip.vue";

export default {
  name: "AvailableCreditsPanel",
  components: {
    BlTextTitle4,
    BlTextBody1,
    BlTextBody2,
    BlIcon,
    BlTooltip
  },
  props: {
    panelId: { type: String }
  },
  data() {
    return {
      selectedId: 0
    };
  },
  computed: {
    ...mapGetters(["currentCenter", "creditTransactionDetails"]),
    availableItems() {
      if (!this.creditTransactionDetails) return [];
      return this.creditTransactionDetails.available_items || [];
    },
    itemOptions() {
      return this.availableItems.map(i => ({
        value: i.package_item_id,
        label: i.package_item_name
      }));
    },
    selected() {
      const found = this.availableItems.find(
        i => i.package_item_id === this.selectedId
      );
      if (!found) return null;
      const creditTransactions = (found.credit_transactions || []).filter(i => {
        return (
          i.total_available_credits > 0 ||
          i.total_reserved_credits > 0 ||
          i.total_unused_credits > 0 ||
          i.current_balance > 0
        );
      });
      const totalAvailableCredits = creditTransactions.reduce(
        (sum, i) => (sum += i.total_available_credits),
        0
      );
      const totalReservedCredits = creditTransactions.reduce(
        (sum, i) => (sum += i.total_reserved_credits),
        0
      );
      const totalUnusedCredits = creditTransactions.reduce(
        (sum, i) => (sum += i.total_unused_credits),
        0
      );
      const currentBalance = creditTransactions.reduce(
        (sum, i) => (sum += i.current_balance),
        0
      );
      return {
        ...found,
        credit_transactions: creditTransactions,
        total_available_credits: totalAvailableCredits,
        total_reserved_credits: totalReservedCredits,
        total_unused_credits: totalUnusedCredits,
        current_balance: currentBalance
      };
    }
  },
  mounted() {
    if (this.availableItems.length) {
      this.selectedId = this.availableItems[0].package_item_id;
    }
  },
  methods: {
    ...mapActions([""]),
    displayDate,
    displayPrice,
    priceFormats(price = 0) {
      const settings = this.currentCenter.currency_settings;
      return this.displayPrice({ settings, price });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/creditTransaction/AvailableCreditsPanel.vue